<template>
  <div class="max-w-[1400px] mx-auto pb-10 h-[calc(100vh-100px)] flex flex-col">

    <!-- Encabezado -->
    <div class="flex items-center justify-between gap-4 mb-6 shrink-0 animate-fade-in">
      <div>
        <h1 class="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
          <MessageSquareSearch class="w-6 h-6 text-indigo-500" />
          Auditoría de Conversaciones
        </h1>
        <p class="text-slate-500 text-sm mt-1">
          Supervisa en tiempo real la actividad de tus agentes clasificada por línea de WhatsApp.
        </p>
      </div>
      <div class="bg-indigo-50 text-indigo-600 px-4 py-2 rounded-xl text-sm font-bold border border-indigo-100 flex items-center gap-2 shadow-sm">
        <Eye class="w-4 h-4" /> Modo Supervisor
      </div>
    </div>

    <!-- Contenedor Principal (Triple Panel) -->
    <div class="flex-1 bg-white rounded-3xl border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden flex animate-fade-in">

      <!-- ======================================================= -->
      <!-- 1. PANEL IZQUIERDO: INSTANCIAS (Líneas)                   -->
      <!-- ======================================================= -->
      <div class="w-20 md:w-64 bg-slate-900 border-r border-slate-800 flex flex-col flex-shrink-0 z-20 transition-all">
        <div class="p-4 border-b border-slate-800 hidden md:block">
          <h2 class="text-xs font-black text-slate-400 uppercase tracking-widest">Tus Líneas</h2>
        </div>

        <div class="flex-1 overflow-y-auto custom-scrollbar p-2 md:p-3 space-y-2">
          <div v-if="isLoadingInstances" class="flex justify-center p-4">
            <Loader2 class="w-5 h-5 text-indigo-400 animate-spin" />
          </div>

          <div v-else-if="instances.length === 0" class="text-center p-4 text-slate-500">
            <Smartphone class="w-6 h-6 mx-auto mb-2 opacity-50" />
            <p class="text-xs hidden md:block">No hay instancias</p>
          </div>

          <button
            v-for="inst in instances"
            :key="inst.id"
            @click="selectInstance(inst)"
            class="w-full flex items-center gap-3 p-3 rounded-xl transition-all group relative"
            :class="selectedInstance?.name === inst.name ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'"
          >
            <Smartphone class="w-5 h-5 flex-shrink-0" :class="selectedInstance?.name === inst.name ? 'text-white' : 'text-indigo-400 group-hover:text-indigo-300'" />
            <span class="font-bold text-sm truncate hidden md:block">{{ inst.name }}</span>
            <ChevronRight v-if="selectedInstance?.name === inst.name" class="w-4 h-4 ml-auto hidden md:block opacity-50" />
          </button>
        </div>
      </div>

      <!-- ======================================================= -->
      <!-- 2. PANEL CENTRAL: CONTACTOS / CHATS                       -->
      <!-- ======================================================= -->
      <div class="w-full md:w-80 border-r border-slate-100 flex flex-col bg-slate-50/80 z-10 flex-shrink-0">

        <div v-if="!selectedInstance" class="flex-1 flex flex-col items-center justify-center p-6 text-center text-slate-400">
          <Smartphone class="w-12 h-12 mb-3 opacity-20" />
          <p class="text-sm font-bold">Selecciona una instancia</p>
          <p class="text-xs mt-1">Para ver sus conversaciones.</p>
        </div>

        <template v-else>
          <!-- Buscador -->
          <div class="p-4 border-b border-slate-200 bg-white">
            <div class="relative">
              <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Buscar cliente o grupo..."
                class="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-slate-700"
              />
            </div>
          </div>

          <!-- Lista de Chats -->
          <div class="flex-1 overflow-y-auto custom-scrollbar">
            <div v-if="isLoadingContacts" class="flex justify-center p-8">
              <Loader2 class="w-6 h-6 text-indigo-500 animate-spin" />
            </div>

            <div v-else-if="filteredContacts.length === 0" class="text-center p-8 text-slate-400">
              <Users class="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p class="text-sm">Sin chats activos.</p>
            </div>

            <div v-else class="divide-y divide-slate-100">
              <button
                v-for="contact in filteredContacts"
                :key="contact.id"
                @click="selectChat(contact)"
                class="w-full text-left p-4 hover:bg-white transition-colors relative flex gap-3 items-center"
                :class="selectedContact?.id === contact.id ? 'bg-white border-l-4 border-indigo-500 shadow-sm' : 'border-l-4 border-transparent'"
              >
                <!-- Avatar del Cliente o Grupo -->
                <div class="relative flex-shrink-0">
                  <img :src="contact.profilePicUrl" class="w-10 h-10 rounded-full object-cover border border-slate-200" />
                  <div v-if="contact.isGroup" class="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm border border-slate-200">
                    <Users class="w-3 h-3 text-indigo-500" />
                  </div>
                </div>

                <div class="flex-1 overflow-hidden">
                  <div class="flex justify-between items-start mb-1">
                    <!-- Nombre / Teléfono -->
                    <div class="flex flex-col overflow-hidden pr-2">
                      <h4 class="font-bold text-slate-900 text-sm truncate">{{ contact.customer_phone }}</h4>
                      <span v-if="contact.customer_name" class="text-[10px] text-slate-500 truncate">{{ contact.customer_name }}</span>
                    </div>
                    <span class="text-[10px] font-semibold text-slate-400 whitespace-nowrap">{{ formatDate(contact.last_message_at) }}</span>
                  </div>

                  <div class="flex items-center gap-2 mt-1">
                    <span class="px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-md text-[9px] font-bold flex items-center gap-1 border border-indigo-100 uppercase tracking-wider flex-shrink-0">
                      <Bot class="w-3 h-3" /> {{ contact.agent_name || 'IA' }}
                    </span>
                    <!-- Pre-visualizador limpio -->
                    <p class="text-xs text-slate-500 truncate flex-1">
                      {{ contact.last_message }}
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </template>
      </div>

      <!-- ======================================================= -->
      <!-- 3. PANEL DERECHO: HISTORIAL ENRIQUECIDO                   -->
      <!-- ======================================================= -->
      <div class="hidden md:flex flex-1 flex-col bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] bg-slate-50/50 relative">

        <div v-if="!selectedContact" class="flex-1 flex flex-col items-center justify-center text-slate-400">
          <div class="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-sm mb-4 border border-slate-100">
            <MessageSquareSearch class="w-10 h-10 text-indigo-200" />
          </div>
          <p class="font-medium text-slate-500">Auditoría en Espera</p>
          <p class="text-xs mt-1 text-center max-w-xs">Selecciona un cliente para leer la conversación completa.</p>
        </div>

        <template v-else>
          <!-- Chat Header -->
          <div class="h-16 px-6 bg-white border-b border-slate-200 flex items-center justify-between shadow-sm z-10 shrink-0">
            <div class="flex items-center gap-4">
              <!-- FOTO DEL ENCABEZADO -->
              <div class="relative w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center border border-slate-200 shadow-sm flex-shrink-0">
                <img :src="selectedContact.profilePicUrl" class="w-full h-full rounded-full object-cover" />
                <div v-if="selectedContact.isGroup" class="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm border border-slate-200">
                   <Users class="w-3 h-3 text-indigo-500" />
                </div>
              </div>

              <div class="flex flex-col">
                <div class="flex items-center gap-2">
                  <h3 class="font-bold text-slate-900 leading-tight">{{ selectedContact.customer_phone }}</h3>
                  <span v-if="selectedContact.customer_name" class="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md text-[10px] font-bold truncate max-w-[120px]">
                    {{ selectedContact.customer_name }}
                  </span>
                </div>
                <p class="text-[11px] text-slate-500 font-medium mt-0.5">Atendido por: <span class="text-indigo-600 font-bold uppercase tracking-wider">{{ selectedContact.agent_name || 'Agente IA' }}</span></p>
              </div>
            </div>
          </div>

          <!-- Mensajes Renderizados -->
          <div class="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar" id="chatContainer">

            <div v-if="isLoadingMessages" class="flex justify-center p-4">
              <Loader2 class="w-6 h-6 text-indigo-500 animate-spin" />
            </div>

            <template v-else>
              <div v-for="msg in messages" :key="msg.id" class="flex flex-col w-full animate-fade-in" :class="msg.sender === 'bot' ? 'items-end' : 'items-start'">

                <div class="flex gap-2 max-w-[75%]" :class="msg.sender === 'bot' ? 'flex-row-reverse' : ''">
                  <!-- Avatar miniatura en mensaje -->
                  <div class="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center mt-auto shadow-sm" :class="msg.sender === 'bot' ? 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md' : 'bg-slate-200 border border-slate-300'">
                    <Bot v-if="msg.sender === 'bot'" class="w-3 h-3 text-white" />
                    <User v-else class="w-3 h-3 text-slate-500" />
                  </div>

                  <!-- Contenedor del Mensaje -->
                  <div>
                    <!-- AUTOR DEL MENSAJE (Solo visible en grupos) -->
                    <span v-if="msg.senderName" class="text-[10px] font-bold text-indigo-500 mb-0.5 block ml-1">
                      ~ {{ msg.senderName }}
                    </span>

                    <div class="px-3 py-2.5 rounded-2xl shadow-sm text-sm"
                         :class="[
                           msg.sender === 'bot' ? 'bg-gradient-to-br from-indigo-600 to-purple-700 text-white rounded-br-sm shadow-indigo-500/20' : 'bg-white border border-slate-200 text-slate-700 rounded-bl-sm',
                           msg.type === 'sticker' ? '!bg-transparent !border-0 !shadow-none !p-0' : ''
                         ]">

                      <!-- ================================== -->
                      <!-- RENDERIZADOR DE MEDIOS ENRIQUECIDO -->
                      <!-- ================================== -->

                      <!-- 1. IMAGEN -->
                      <div v-if="msg.type === 'image'" class="flex flex-col gap-2">
                        <img v-if="msg.mediaUrl" :src="msg.mediaUrl" class="rounded-xl max-w-[250px] object-contain bg-black/10 border border-white/10" />
                        <div v-else class="flex items-center gap-2 p-2 rounded-lg text-xs font-bold" :class="msg.sender === 'bot' ? 'bg-white/10' : 'bg-indigo-50 text-indigo-600'">
                          <ImageIcon class="w-4 h-4" /> <span>Fotografía Enviada</span>
                        </div>
                        <p v-if="msg.text" class="whitespace-pre-wrap">{{ msg.text }}</p>
                      </div>

                      <!-- 2. UBICACIÓN (GPS) -->
                      <div v-else-if="msg.type === 'location'" class="flex items-center gap-3 p-2 rounded-xl" :class="msg.sender === 'bot' ? 'bg-white/10' : 'bg-emerald-50'">
                        <div class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" :class="msg.sender === 'bot' ? 'bg-white/20' : 'bg-emerald-200 text-emerald-700'">
                          <MapPin class="w-4 h-4" />
                        </div>
                        <div>
                          <a :href="`https://www.google.com/maps/search/?api=1&query=${msg.lat},${msg.lng}`" target="_blank" class="font-bold text-xs hover:underline" :class="msg.sender === 'bot' ? 'text-white' : 'text-emerald-700'">
                            Abrir en Google Maps
                          </a>
                          <p class="text-[10px] opacity-80 line-clamp-1 mt-0.5">{{ msg.text }}</p>
                        </div>
                      </div>

                      <!-- 3. AUDIO / NOTA DE VOZ -->
                      <div v-else-if="msg.type === 'audio'" class="flex items-center gap-2 px-2 py-1">
                        <Mic class="w-4 h-4" :class="msg.sender === 'bot' ? 'text-white' : 'text-sky-500'" />
                        <span class="text-xs font-medium opacity-90">Mensaje de voz</span>
                      </div>

                      <!-- 4. VIDEO -->
                      <div v-else-if="msg.type === 'video'" class="flex flex-col gap-2">
                        <img v-if="msg.mediaUrl" :src="msg.mediaUrl" class="rounded-xl max-w-[200px] object-cover opacity-80 relative" />
                        <div v-else class="flex items-center gap-2 p-2 rounded-lg" :class="msg.sender === 'bot' ? 'bg-white/10' : 'bg-rose-50 text-rose-600'">
                          <Video class="w-4 h-4" /> <span class="text-xs font-bold">Video</span>
                        </div>
                        <p v-if="msg.text" class="whitespace-pre-wrap">{{ msg.text }}</p>
                      </div>

                      <!-- 5. DOCUMENTO -->
                      <div v-else-if="msg.type === 'document'" class="flex items-center gap-2 p-2 rounded-lg" :class="msg.sender === 'bot' ? 'bg-white/10' : 'bg-orange-50 text-orange-600'">
                        <FileText class="w-5 h-5 flex-shrink-0" />
                        <span class="text-xs font-medium truncate max-w-[150px]" :title="msg.fileName">{{ msg.fileName }}</span>
                      </div>

                      <!-- 6. STICKER -->
                      <div v-else-if="msg.type === 'sticker'" class="p-2">
                         <Smile class="w-12 h-12" :class="msg.sender === 'bot' ? 'text-indigo-200' : 'text-slate-400'" />
                      </div>

                      <!-- 7. TEXTO PLANO -->
                      <div v-else class="whitespace-pre-wrap">{{ msg.text }}</div>

                    </div>

                    <!-- Fecha y Hora -->
                    <span class="text-[9px] font-bold text-slate-400 mt-1 block uppercase" :class="msg.sender === 'bot' ? 'text-right mr-1' : 'ml-1'">
                      {{ formatTime(msg.created_at) }}
                    </span>
                  </div>

                </div>
              </div>
            </template>
          </div>

        </template>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, nextTick } from 'vue';
import {
  MessageSquareSearch, Search, Bot, User, Loader2, Eye, Users,
  Smartphone, ChevronRight, Image as ImageIcon, MapPin, Mic, Video, FileText, Smile
} from 'lucide-vue-next';
import apiClient from '@/api/client';
import axios from 'axios';
import { SecureStorage } from '@/utils/secureStorage';
import { useToast } from '@/composables/useToast';

const currentUser = SecureStorage.getItem('neurals_user');
const { showError } = useToast();

const EVO_URL = 'https://wapi.therealdevs.pro';
const EVO_KEY = 'TheRealDevsPro2026';
const getHeaders = () => ({ headers: { apikey: EVO_KEY } });

const instances = ref([]);
const selectedInstance = ref(null);
const contacts = ref([]);
const selectedContact = ref(null);
const searchQuery = ref('');
const messages = ref([]);

const isLoadingInstances = ref(false);
const isLoadingContacts = ref(false);
const isLoadingMessages = ref(false);

// ============================================================================
// 1. CARGAR INSTANCIAS (LÍNEAS)
// ============================================================================
const loadInstances = async () => {
  isLoadingInstances.value = true;
  try {
    const queryParams = currentUser?.role_id === 3 ? { owner: currentUser.id } : {};
    const res = await apiClient.get('/instances', { params: queryParams });
    instances.value = Array.isArray(res.data) ? res.data : (res.data.data || []);
  } catch (error) {
    showError('Error cargando tus líneas de WhatsApp.');
  } finally {
    isLoadingInstances.value = false;
  }
};

onMounted(loadInstances);

// ============================================================================
// 2. CARGAR CONTACTOS REALES DESDE BD + FOTOS OFICIALES DE WHATSAPP
// ============================================================================
const selectInstance = async (inst) => {
  if (selectedInstance.value?.id === inst.id) return;

  selectedInstance.value = inst;
  selectedContact.value = null;
  messages.value = [];
  searchQuery.value = '';

  isLoadingContacts.value = true;
  try {
    // 1. Obtener clientes reales desde nuestro handler de BD (adiós números fantasma)
    const res = await apiClient.get(`/conversations/chats?instance_name=${inst.name}`);
    const dbChats = res.data?.data || [];

    // 2. Obtener fotos de WhatsApp concurrentemente sólo para nuestros contactos reales
    const contactsWithRealPhotos = await Promise.all(
      dbChats.map(async (chat) => {
        const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(chat.customer_name || chat.customer_phone)}&background=6366f1&color=fff`;
        let realPic = fallbackAvatar;

        try {
          const cleanNumber = chat.customer_phone.replace(/\D/g, '');
          const evoPicRes = await axios.post(
            `${EVO_URL}/chat/fetchProfilePictureUrl/${inst.name}`,
            { number: cleanNumber },
            getHeaders()
          );

          if (evoPicRes.data?.profilePictureUrl) {
            realPic = evoPicRes.data.profilePictureUrl;
          }
        } catch {
          // Si no tiene foto pública o hay error, se queda con el avatar con iniciales
          realPic = fallbackAvatar;
        }

        return {
          ...chat,
          profilePicUrl: realPic,
          isGroup: false // Si luego añades grupos a tu BD, puedes ajustar esto
        };
      })
    );

    contacts.value = contactsWithRealPhotos;

  } catch (error) {
    console.error(error);
    showError('Error cargando las conversaciones.');
  } finally {
    isLoadingContacts.value = false;
  }
};

const filteredContacts = computed(() => {
  if (!searchQuery.value) return contacts.value;
  const q = searchQuery.value.toLowerCase();
  return contacts.value.filter(c =>
    c.customer_phone?.toLowerCase().includes(q) ||
    c.customer_name?.toLowerCase().includes(q)
  );
});

// ============================================================================
// 3. CARGAR MENSAJES DESDE EL HANDLER DE BD
// ============================================================================
const selectChat = async (contact) => {
  selectedContact.value = contact;
  isLoadingMessages.value = true;
  messages.value = [];

  try {
    const phoneOnly = contact.customer_phone.replace(/\D/g, '');
    const currentInstance = selectedInstance.value.name;

    // Petición al endpoint de mensajes del nuevo handler
    const res = await apiClient.get(`/conversations/messages?phone=${phoneOnly}&instance_name=${currentInstance}`);
    const dbMessages = res.data?.data || [];

    messages.value = dbMessages.map(msg => {
      // 1. Normalizamos el rol (quitamos espacios y pasamos a minúsculas)
      const rawRole = String(msg.role || '').trim().toLowerCase();

      // 2. Evaluamos si es la IA (cubrimos todas las posibles variantes)
     const isBot = rawRole !== 'user';

      return {
        id: msg.id || Math.random().toString(),
        sender: isBot ? 'bot' : 'user',
        text: msg.text,
        type: 'text', // Por ahora lo forzamos a texto plano
        created_at: msg.created_at
      };
    });

    nextTick(() => {
      const container = document.getElementById('chatContainer');
      if (container) container.scrollTop = container.scrollHeight;
    });

  } catch (error) {
    console.error(error);
    showError('No se pudieron cargar los mensajes.');
  } finally {
    isLoadingMessages.value = false;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString.replace(' ', 'T'));
  return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
};

const formatTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString.replace(' ', 'T'));
  return date.toLocaleTimeString('es-NI', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};
</script>

<style scoped>
@keyframes fade-in { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
.animate-fade-in { animation: fade-in 0.2s ease-out forwards; }
.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
</style>